// === LIFETIME STATS ===
// Statistiques agrégées sur toute la vie du joueur (cumulées entre les runs
// ET les sessions). Complémentaires de MetaStats qui tracke surtout les
// événements de jeu côté combat/exploration. Ici on garde :
//  - le temps de jeu cumulé (heartbeat depuis BootScene),
//  - les ressources cumulées récoltées (toutes confondues + par type),
//  - la fréquence des classes jouées (warrior / ranger / medic).
//
// Persisté dans une clé dédiée pour faciliter export/import & reset isolé.

import { ALL_RESOURCES, ResourceType } from './ResourceManager';
import type { SurvivorClass } from './CampManager';

export const LIFETIME_STORAGE_KEY = 'deadtide_lifetime_stats';

export interface LifetimeStatsData {
  /** Temps de jeu cumulé en millisecondes (heartbeat 1s) */
  totalPlayTimeMs: number;
  /** Ressources cumulées récoltées (par type) */
  resourcesCollected: Record<ResourceType, number>;
  /** Total cumulé toutes ressources confondues (raccourci) */
  resourcesTotal: number;
  /** Nombre de fois où chaque classe a été emmenée en run */
  classesPlayed: Record<SurvivorClass, number>;
  /** Première date de jeu enregistrée (ISO) — affichage informatif */
  firstSeenAt: string | null;
  /** Dernière date de jeu enregistrée (ISO) — mis à jour à chaque tick */
  lastSeenAt: string | null;
}

const DEFAULT_DATA: LifetimeStatsData = {
  totalPlayTimeMs: 0,
  resourcesCollected: {
    wood: 0,
    iron: 0,
    cloth: 0,
    crystal: 0,
    cursed_bone: 0,
  },
  resourcesTotal: 0,
  classesPlayed: { warrior: 0, ranger: 0, medic: 0 },
  firstSeenAt: null,
  lastSeenAt: null,
};

function load(): LifetimeStatsData {
  try {
    const raw = localStorage.getItem(LIFETIME_STORAGE_KEY);
    if (!raw) return cloneDefaults();
    const parsed = JSON.parse(raw);
    // Merge défensif : on ne fait pas confiance au shape persisté
    return {
      totalPlayTimeMs: Number(parsed.totalPlayTimeMs) || 0,
      resourcesCollected: {
        ...DEFAULT_DATA.resourcesCollected,
        ...(parsed.resourcesCollected ?? {}),
      },
      resourcesTotal: Number(parsed.resourcesTotal) || 0,
      classesPlayed: {
        ...DEFAULT_DATA.classesPlayed,
        ...(parsed.classesPlayed ?? {}),
      },
      firstSeenAt: typeof parsed.firstSeenAt === 'string' ? parsed.firstSeenAt : null,
      lastSeenAt: typeof parsed.lastSeenAt === 'string' ? parsed.lastSeenAt : null,
    };
  } catch {
    return cloneDefaults();
  }
}

function cloneDefaults(): LifetimeStatsData {
  return {
    totalPlayTimeMs: 0,
    resourcesCollected: { ...DEFAULT_DATA.resourcesCollected },
    resourcesTotal: 0,
    classesPlayed: { ...DEFAULT_DATA.classesPlayed },
    firstSeenAt: null,
    lastSeenAt: null,
  };
}

function persist(data: LifetimeStatsData): void {
  try {
    localStorage.setItem(LIFETIME_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage plein/indispo — on ignore
  }
}

/**
 * Singleton statique : pas besoin de l'injecter partout, et le tick global
 * (BootScene → setInterval) doit pouvoir appeler une méthode sans avoir
 * d'instance sous la main.
 */
export class LifetimeStats {
  private static _data: LifetimeStatsData | null = null;

  private static getData(): LifetimeStatsData {
    if (!LifetimeStats._data) LifetimeStats._data = load();
    return LifetimeStats._data;
  }

  /** Lecture (copie défensive) */
  static getAll(): LifetimeStatsData {
    const d = LifetimeStats.getData();
    return {
      totalPlayTimeMs: d.totalPlayTimeMs,
      resourcesCollected: { ...d.resourcesCollected },
      resourcesTotal: d.resourcesTotal,
      classesPlayed: { ...d.classesPlayed },
      firstSeenAt: d.firstSeenAt,
      lastSeenAt: d.lastSeenAt,
    };
  }

  /**
   * Avance le compteur de temps de jeu. Appelé par un heartbeat global (1s)
   * tant que l'onglet est visible. Update aussi firstSeenAt/lastSeenAt.
   */
  static tick(ms: number): void {
    if (ms <= 0) return;
    const d = LifetimeStats.getData();
    d.totalPlayTimeMs += ms;
    const now = new Date().toISOString();
    if (!d.firstSeenAt) d.firstSeenAt = now;
    d.lastSeenAt = now;
    persist(d);
  }

  /**
   * Incrémente le compteur de ressources collectées. Ignoré pour amount <= 0
   * (les dépenses ne réduisent pas le cumul lifetime).
   */
  static addResource(type: ResourceType, amount: number): void {
    if (amount <= 0) return;
    const d = LifetimeStats.getData();
    d.resourcesCollected[type] = (d.resourcesCollected[type] ?? 0) + amount;
    d.resourcesTotal += amount;
    persist(d);
  }

  /** Variante batch — équivaut à addResource() en boucle, 1 seul save. */
  static addResources(stock: Partial<Record<ResourceType, number>>): void {
    const d = LifetimeStats.getData();
    let any = false;
    for (const t of ALL_RESOURCES) {
      const amount = stock[t];
      if (!amount || amount <= 0) continue;
      d.resourcesCollected[t] = (d.resourcesCollected[t] ?? 0) + amount;
      d.resourcesTotal += amount;
      any = true;
    }
    if (any) persist(d);
  }

  /**
   * Marque qu'une classe a été emmenée pour ce run. À appeler 1 fois par
   * membre d'équipe au démarrage du run.
   */
  static recordClassPlayed(cls: SurvivorClass): void {
    const d = LifetimeStats.getData();
    d.classesPlayed[cls] = (d.classesPlayed[cls] ?? 0) + 1;
    persist(d);
  }

  /** Retourne la classe la plus jouée, ou null si jamais joué */
  static getMostPlayedClass(): { cls: SurvivorClass; count: number } | null {
    const d = LifetimeStats.getData();
    let best: { cls: SurvivorClass; count: number } | null = null;
    for (const cls of Object.keys(d.classesPlayed) as SurvivorClass[]) {
      const count = d.classesPlayed[cls];
      if (count > 0 && (!best || count > best.count)) {
        best = { cls, count };
      }
    }
    return best;
  }

  /** Reset complet (debug + reset global) */
  static resetAll(): void {
    LifetimeStats._data = cloneDefaults();
    persist(LifetimeStats._data);
  }

  /**
   * Recharge depuis localStorage — utilisé après un import de sauvegarde
   * qui écrit les clés directement sans passer par cette API.
   */
  static reload(): void {
    LifetimeStats._data = load();
  }
}

/**
 * Formate une durée en ms vers une chaîne lisible : "12j 4h 32m", "3h 15m",
 * "42m 18s", "23s". Utilisé pour l'affichage du temps de jeu cumulé.
 */
export function formatPlayTime(ms: number): string {
  if (ms < 1000) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  if (days > 0) return `${days}j ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}
